/** One ephemeral id per document, so duplicated tabs cannot share a tab id. */
export type PresenceArea = 'landing' | 'course' | 'public';
export type PresenceLevel = { level: number; track: string };

/** The level this document has open, shared by every presence loop in it. */
let openLevel: PresenceLevel | null = null;
const levelListeners = new Set<() => void>();
export function setPresenceLevel(next: PresenceLevel | null): void {
  if (openLevel?.level === next?.level && openLevel?.track === next?.track) return;
  openLevel = next ? { level: next.level, track: next.track } : null;
  for (const listener of levelListeners) listener();
}

export function beginPresence(area: PresenceArea, identity: () => { visitor: string; browser: string }, optedOut: () => boolean): () => void {
  if (optedOut()) return () => {};
  const session = Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, '0')).join('');
  let sequence = 0;
  let lastInteraction = performance.now();
  let timer: ReturnType<typeof setInterval> | null = null;
  let stopped = false;
  const cancel = () => { if (timer !== null) clearInterval(timer); timer = null; };
  const send = (visible: boolean) => {
    if (stopped) return;
    if (optedOut()) { stopped = true; cancel(); return; }
    try {
      void fetch('/api/events/presence', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...identity(), session, area, visible, sequence: ++sequence,
          ...(area === 'course' && openLevel ? openLevel : {}),
          idleMs: Math.min(86_400_000, Math.max(0, Math.floor(performance.now() - lastInteraction))) }),
        credentials: 'omit', cache: 'no-store', keepalive: true,
      }).then(res => { if (res.status === 404) { stopped = true; cancel(); } }).catch(() => {});
    } catch { /* Measurement must never affect the page. */ }
  };
  const visibility = () => {
    cancel();
    const visible = document.visibilityState === 'visible';
    send(visible);
    if (visible && !stopped) timer = setInterval(() => send(true), 30_000);
  };
  const leave = () => { cancel(); send(false); };
  const interact = () => { lastInteraction = performance.now(); };
  // Moving between levels reports at once rather than on the next 30s beat.
  const levelChanged = () => { if (timer !== null) send(true); };
  levelListeners.add(levelChanged);
  const inputs = ['pointerdown', 'keydown', 'scroll', 'touchstart'] as const;
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('pagehide', leave);
  window.addEventListener('pageshow', visibility);
  for (const type of inputs) window.addEventListener(type, interact, { passive: true, capture: true });
  visibility();
  return () => {
    leave(); stopped = true;
    levelListeners.delete(levelChanged);
    document.removeEventListener('visibilitychange', visibility);
    window.removeEventListener('pagehide', leave);
    window.removeEventListener('pageshow', visibility);
    for (const type of inputs) window.removeEventListener(type, interact, true);
  };
}
